import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle, XCircle, Loader2, ArrowRight, Sparkles, CreditCard } from 'lucide-react';
import { paymentsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const PaymentResult = ({ onNavigate }) => {
    const { isAuthenticated } = useAuth();
    const [status, setStatus] = useState('loading'); // loading, success, error, cancelled
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [pollCount, setPollCount] = useState(0);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get('session_id');
        const path = window.location.pathname;

        // Check if it's a cancel path
        if (path === '/payment/cancel') {
            setStatus('cancelled');
            return;
        }

        // Need to be authenticated to check payment status
        if (!isAuthenticated) {
            setStatus('error');
            return;
        }

        if (sessionId) {
            pollPaymentStatus(sessionId);
        } else {
            setStatus('error');
        }
    }, [isAuthenticated]);

    const pollPaymentStatus = async (sessionId, attempt = 0) => {
        const maxAttempts = 10;
        const pollInterval = 2000;

        if (attempt >= maxAttempts) {
            setStatus('error');
            toast.error('Payment verification timed out. Please check your email for confirmation.');
            return;
        }

        try {
            const response = await paymentsApi.getCheckoutStatus(sessionId);
            const data = response.data;

            setPollCount(attempt + 1);

            if (data.payment_status === 'paid' || data.status === 'completed') {
                setStatus('success');
                setPaymentDetails(data);
                toast.success('Payment successful!');
                return;
            } else if (data.status === 'expired') {
                setStatus('error');
                toast.error('Payment session expired');
                return;
            }

            // Continue polling if still pending
            setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
        } catch (error) {
            console.error('Error checking payment status:', error);
            if (attempt < 3) {
                setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
            } else {
                setStatus('error');
            }
        }
    };

    if (status === 'loading') {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6">
                <Card className="w-full max-w-md text-center">
                    <CardHeader>
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
                            <Loader2 className="h-8 w-8 text-accent animate-spin" />
                        </div>
                        <CardTitle className="font-serif text-2xl">Processing Payment</CardTitle>
                        <CardDescription>
                            Verifying your payment with Stripe...
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="w-full bg-muted rounded-full h-2 mb-4">
                            <div 
                                className="bg-accent h-2 rounded-full transition-all duration-500"
                                style={{ width: `${Math.min(pollCount * 10, 90)}%` }}
                            />
                        </div>
                        <p className="text-sm text-muted-foreground">
                            This may take a few moments...
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (status === 'success') {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6">
                <Card className="w-full max-w-md text-center">
                    <CardHeader>
                        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                            <CheckCircle className="h-10 w-10 text-green-600" />
                        </div>
                        <CardTitle className="font-serif text-2xl text-green-600">Payment Successful!</CardTitle>
                        <CardDescription>
                            Thank you for your purchase. Your account has been updated.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {paymentDetails && (
                            <div className="p-4 bg-muted/50 rounded-lg">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm text-muted-foreground">Amount Paid</span>
                                    <span className="font-semibold">
                                        ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-muted-foreground">Status</span>
                                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
                                        Completed
                                    </Badge>
                                </div>
                            </div>
                        )}

                        <div className="pt-4 space-y-3">
                            <Button 
                                className="w-full bg-accent hover:bg-accent/90"
                                onClick={() => onNavigate('dashboard')}
                            >
                                <Sparkles className="h-4 w-4 mr-2" />
                                Start Writing
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                            <Button 
                                variant="outline"
                                className="w-full"
                                onClick={() => onNavigate('credits')}
                            >
                                <CreditCard className="h-4 w-4 mr-2" />
                                View My Credits
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (status === 'cancelled') {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6">
                <Card className="w-full max-w-md text-center">
                    <CardHeader>
                        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                            <XCircle className="h-10 w-10 text-amber-600" />
                        </div>
                        <CardTitle className="font-serif text-2xl">Payment Cancelled</CardTitle>
                        <CardDescription>
                            No worries! You can try again anytime or continue with the free plan.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <Button 
                            className="w-full bg-accent hover:bg-accent/90"
                            onClick={() => onNavigate('landing')}
                        >
                            View Plans Again
                        </Button>
                        <Button 
                            variant="outline"
                            className="w-full"
                            onClick={() => onNavigate('dashboard')}
                        >
                            Continue with Free Plan
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Error state
    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
            <Card className="w-full max-w-md text-center">
                <CardHeader>
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                        <XCircle className="h-10 w-10 text-red-600" />
                    </div>
                    <CardTitle className="font-serif text-2xl text-red-600">Payment Error</CardTitle>
                    <CardDescription>
                        We couldn't verify your payment. If you were charged, please contact support.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <Button 
                        className="w-full"
                        onClick={() => onNavigate('landing')}
                    >
                        Try Again
                    </Button>
                    <Button 
                        variant="outline"
                        className="w-full"
                        onClick={() => onNavigate('dashboard')}
                    >
                        Go to Dashboard
                    </Button>
                    <p className="text-sm text-muted-foreground">
                        Need help? Contact us at support@eskrybe.com
                    </p>
                </CardContent>
            </Card>
        </div>
    );
};

export default PaymentResult;
